import { BaseViewStore } from 'core/stores';
import { OrderStore } from 'modules/order/stores';
import { ReturnLabelForm } from 'modules/order/forms';
import { observable, action, makeObservable } from 'mobx';
import { RateServiceStore } from 'modules/rate/stores';
import { lookupRouteName, orderStatus, sourceOfChange } from 'common/constants';
import { LookupStore } from 'modules/lookup/stores';
import { translate, createFileUrl, mapCarrierServiceWithRates, PiiDataValidator } from 'core/utils';
import { ReturnAddressStore } from 'modules/return-address/stores';
import { DimensionsStepForm as BoxForm } from '../forms/labeling-mode';
import { AddressAutofillViewStore, BaasicDropdownViewStore } from 'common/stores';
import * as printJS from 'print-js';
import _ from 'lodash';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { ReturnAddressEdit } from 'modules/return-address/pages';
import AddNewTemplateDropdownAction from 'core/components/select/actions/AddNewTemplateDropdownAction';

class ReturnLabelViewStore extends BaseViewStore {
	@observable selectedOrder;
	@observable isFormValid = false;
	@observable isCarrierServiceLoading = false;
	@observable isCreateLabelLoading = false;
	@observable infiniteSearchPhrase = null;
	@observable pdfURLReturn = null;
	@observable isBoxRequired = false;
	@observable noRatesMessage;
	@observable onePerBoxMessage;
	@observable showCreateTemplate = false;
	debouncedGetRates = null;
	packageInformation = null;

	form = new ReturnLabelForm({
		onSuccess: async () => {
			await this.createReturnLabel();
		},
	});

	boxForm = new BoxForm({
		onSuccess: async () => {
			await this.onCreateTemplateSubmit();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.orderStore = new OrderStore(rootStore);
		this.rateServiceStore = new RateServiceStore(rootStore);
		this.shipmentInfoReturnStore = new ShipmentInfoStore(rootStore);
		this.returnAddressStore = new ReturnAddressStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.piiDataValidator = new PiiDataValidator(rootStore);
		this.addressAutofillViewStore = new AddressAutofillViewStore(this.rootStore, {
			additionalEvents: { onInputChange: this.onAddressDataChange },
			parentViewData: {
				buyerName: this.form.$('buyerName'),
				address1: this.form.$('address1'),
				address2: this.form.$('address2'),
				city: this.form.$('city'),
				zipCode: this.form.$('zipCode'),
				isBaasicDropdown: false,
				countryCode: this.form.$('countryCode'),
				stateCode: this.form.$('countryStateCode'),
			},
		});
		this.initialize(this.rootStore.routerStore.routerState.params.id);
	}

	@action.bound
	initialize = async orderId => {
		this.suspend();
		await this.getOrder(orderId);
		await this.initializeDropdowns();
		const defaultReturnAddress = await this.getDefaultReturnAddress();
		this.returnAddressDropDownStore = new BaasicDropdownViewStore({
			field: this.form.$('returnAddressId'),
			store: this.returnAddressStore,
			value: defaultReturnAddress
				? { label: defaultReturnAddress.name, value: defaultReturnAddress.id, extra: defaultReturnAddress }
				: null,
			filterProperties: {
				sellerIds: [this.selectedOrder.seller.id],
				embed: ['seller', 'address', 'address.coreCountry', 'address.coreCountryState'],
			},
			isRequired: true,
			onChangeCallback: async (selectedOption, field) => {
				field.set('value', selectedOption.value);
				this.form.$('carrierServiceId').set('rules', '');
				await this.validateForm();
				if (this.isFormValid) {
					await this.getRates();
				}
				this.form.$('carrierServiceId').set('rules', 'required');
			},
		});
		await this.updateForm(this.selectedOrder);
		this.calculateCheckedQuantity(this.selectedOrder);
		this.debouncedGetRates = _.debounce(() => {
			this.getRates();
		}, 700);

		if (this.selectedOrder.box != null) {
			this.boxDropdownStore.setValue(this.selectedOrder.box);
		}

		this.boxForm.$('boxId').set('rules', this.isBoxRequired ? 'required' : '');
		this.resume();
	};

	@action.bound
	initializeDropdowns = async () => {
		this.boxDropdownStore = new BaasicDropdownViewStore({
			field: this.boxForm.$('boxId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.box,
			onChangeCallback: value => this.onBoxChange(value),
			headerOption: { value: null, label: 'Add new template' },
		});
		this.returnReasonDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('returnReasonId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.returnReason,
			onChangeCallback: value => this.onReturnReasonSelect(value),
		});
		this.carrierServiceDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('carrierServiceId'),
			staticOptions: [],
		});
	};

	addNewTemplateOptionOverride = {
		shouldDisplayCallback: value => {
			return _.get(value, 'label') === 'Add new template';
		},
		Component: AddNewTemplateDropdownAction,
	};

	@action.bound
	getOrder = async id => {
		this.selectedOrder = await this.orderStore.getOrder(id, {
			embed: [
				'channel',
				'channel.channelType',
				'box',
				'seller',
				'shippingCoreCountry',
				'shippingCoreCountryState',
				'carrierService',
				'carrier',
				'orderLines',
				'orderLines.product',
				'orderStatus',
				'shipmentInfos',
				'shipmentInfos.returnedItems',
				'shipmentInfos.returnedItems.product',
			],
		});
		this.selectedOrder.orderLines = _.filter(this.selectedOrder.orderLines, orderLine => {
			return orderLine.quantity > 0 && orderLine.productId;
		});
	};

	@action.bound
	getDefaultReturnAddress = async () => {
		await this.returnAddressStore.findReturnAddress({
			sellerIds: [this.selectedOrder.seller.id],
			page: 1,
			rpp: 25,
			isDefaultReturnAddress: true,
			embed: ['seller', 'address', 'address.coreCountry', 'address.coreCountryState'],
		});
		return this.returnAddressStore.data[0];
	};

	openAddReturnAddressModal = () => {
		this.openModal({
			onSuccess: async () => {
				this.suspend();
				await this.returnAddressDropDownStore.reload();
				this.resume();
			},
			data: this,
			component: ReturnAddressEdit,
		});
	};

	@action.bound
	updateForm = async () => {
		this.suspend();
		let formData = this.piiDataValidator.checkIfUncodedPiiDataExists(this.selectedOrder) ? this.selectedOrder : null;
		if (this.selectedOrder.orderStatus.abrv !== orderStatus.pending && formData) {
			// Reverses shipper and recipient information. If the value is not provided, set an empty string.
			this.form.$('buyerName').value = formData.buyerName || '';
			this.form.$('phone1').value = formData.buyerPhone || '';
			this.form.$('address1').value = formData.shippingAddress1 || '';
			this.form.$('address2').value = formData.shippingAddress2 || '';
			this.form.$('address3').value = formData.shippingAddress3 || '';
			this.form.$('city').value = formData.shippingCity || '';
			this.form.$('zipCode').value = formData.shippingZipCode || '';
			this.form.$('countryCode').value = formData.shippingCoreCountry.countryCode || '';
			this.form.$('countryStateCode').value = formData.shippingCoreCountryState
				? formData.shippingCoreCountryState.countryStateCode
				: '';
		}

		if (this.selectedOrder.orderLines.length === 1) {
			if (this.selectedOrder.orderLines[0].product.onePerBox) {
				this.selectedOrder.orderLines[0].isChecked = true;
				this.selectedOrder.orderLines[0].isPartiallyChecked = false;
				this.selectedOrder.orderLines[0].checkedQuantity = 1;
				this.onePerBoxMessage = 'ORDERVIEW.RETURNLABELVIEW.ONEPERBOXMESSAGE';
				this.isBoxRequired = false;
			} else {
				this.onePerBoxMessage = '';
				this.selectedOrder.orderLines[0].checkedQuantity = this.selectedOrder.orderLines[0].quantity;
				if (this.selectedOrder.orderLines[0].checkedQuantity > 1) {
					this.isBoxRequired = true;
				}
			}
		}

		// Initial carrier service value.
		this.carrierServiceDropdownStore.value = {
			value: null,
			label: `${translate('ORDERVIEW.RETURNLABELVIEW.ALLFIELDSREQUIRED')}`,
		};

		this.resume();
	};

	@action.bound
	validateForm = async () => {
		await this.form.validate().then(({ isValid }) => {
			if (!isValid) {
				this.isFormValid = isValid;
				this.form.showErrors();
			} else {
				this.isFormValid = isValid;
			}
		});
	};

	@action.bound
	createReturnLabel = async () => {
		this.suspend();
		this.isCreateLabelLoading = true;
		try {
			const { carrierServiceId, returnReasonId, internalNote, returnAddressId } = this.form.values();
			const shipperInformation = this.composeShipperInformation();
			// Find selected carrier service in available options to get rate from extra property.
			const selectedCarrierServiceRate = _.find(
				this.carrierServiceDropdownStore.options,
				item =>
					item.value === carrierServiceId &&
					(_.get(this.carrierServiceDropdownStore, 'value.extra.carrier.id') == null ||
						_.get(this.carrierServiceDropdownStore, 'value.extra.carrier.id') === item.extra.carrier.id)
			);
			const assignedCarrierId =
				selectedCarrierServiceRate &&
				selectedCarrierServiceRate.extra != null &&
				selectedCarrierServiceRate.extra.carrier != null
					? selectedCarrierServiceRate.extra.carrier.id
					: selectedCarrierServiceRate.extra != null && selectedCarrierServiceRate.extra.carrierId != null
					? selectedCarrierServiceRate.extra.carrierId
					: null;
			this.packageInformation = this.isBoxRequired
				? {
						boxId: this.boxForm.$('boxId').value,
						weight: this.selectedOrder.orderLines
							.filter(i => i.checkedQuantity > 0)
							.reduce((acc, curr) => acc + curr.product.weight * curr.checkedQuantity, 0),
				  }
				: undefined;
			const response = await this.shipmentInfoReturnStore.createReturnLabel({
				orderId: this.selectedOrder.id,
				shipperInformation: shipperInformation,
				carrierServiceId,
				carrierId: assignedCarrierId,
				internalNote,
				returnReasonId,
				returnAddressId: returnAddressId,
				packageInformation: this.packageInformation,
				itemsForReturn: this.getItemsForReturn(),
			});
			this.rootStore.notificationStore.success([
				'ORDERVIEW.RETURNLABELVIEW.SUCCESS',
				{ id: this.selectedOrder.orderNumber },
			]);
			if (response) {
				const label = response.shipmentData.label;
				this.pdfURLReturn = createFileUrl(label);
				printJS({
					printable: this.pdfURLReturn,
					onPrintDialogClose: () => {
						this.rootStore.routerStore.goBack();
					},
				});
			}
		} catch (err) {
			this.rootStore.notificationStore.error([
				'ORDERVIEW.RETURNLABELVIEW.FAIL',
				{ id: this.selectedOrder.orderNumber },
			]);
		}
		this.isCreateLabelLoading = false;
		this.resume();
	};

	onBack = () => {
		this.rootStore.routerStore.goBack();
	};

	@action.bound
	async onReturnReasonSelect(option) {
		if (option != null) {
			this.form.$('carrierServiceId').set('rules', '');
			await this.validateForm();
			if (this.isFormValid) {
				await this.getRates();
			}
			this.form.$('carrierServiceId').set('rules', 'required');
		}
	}

	@action.bound
	onAddressDataChange = async () => {
		this.form.$('carrierServiceId').set('rules', '');
		await this.validateForm();
		if (this.isFormValid) {
			this.debouncedGetRates();
		}
		this.form.$('carrierServiceId').set('rules', 'required');
	};

	@action.bound
	getRates = async (searchPhrase = null) => {
		// If none of the items is selected, do not calcualte rate since data is missing.
		if (!_.every(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity === 0)) {
			this.noRatesMessage = '';
			this.isCarrierServiceLoading = true;
			this.carrierServiceDropdownStore.value = {
				value: null,
				label: `${translate('ORDERVIEW.RETURNLABELVIEW.CALCULATING')}`,
			};
			this.form.$('carrierServiceId').set('rules', 'required');
			let options = [];
			const shipperInformation = this.composeShipperInformation();
			let selectedProductIds = [];
			_.forEach(this.selectedOrder.orderLines, orderLine => {
				if (orderLine.checkedQuantity > 0) {
					selectedProductIds.push(orderLine.productId);
				}
			});

			if (this.form.$('returnAddressId').value !== null) {
				await this.rateServiceStore.getReturnRates({
					orderId: this.selectedOrder.id,
					addressId: this.returnAddressDropDownStore.value.extra.addressId,
					boxId: this.boxForm.$('boxId').value,
					shipperInformation: shipperInformation,
					returnedItems: this.getItemsForReturn(),
				});
				options = mapCarrierServiceWithRates(this.rateServiceStore.response);
			}
			this.carrierServiceDropdownStore.options = [...options];
			this.isCarrierServiceLoading = false;

			if (this.carrierServiceDropdownStore.options.length > 0 && !this.isCarrierServiceLoading) {
				// If rates exist, the cheapest is auto selected.
				const cheapestCarrierService = _.orderBy(
					this.carrierServiceDropdownStore.options,
					['extra.rateComposition.total'],
					['asc']
				)[0];
				this.carrierServiceDropdownStore.setValue(cheapestCarrierService);
			} else if (
				this.carrierServiceDropdownStore.options.length == 0 &&
				selectedProductIds.length > 1 &&
				!this.isCarrierServiceLoading
			) {
				// Else if rates exist and multiple SKUs are selected, message is displayed inidicating there is no rate or no matching carrier service per virtual SKU.
				this.carrierServiceDropdownStore.clear();
				this.noRatesMessage = 'ORDERVIEW.RETURNLABELVIEW.NOMATCHINGCARRIERS';
			} else if (!this.isCarrierServiceLoading) {
				// Else no rate message is displayed.
				this.carrierServiceDropdownStore.clear();
				this.noRatesMessage = 'ORDERVIEW.RETURNLABELVIEW.NORATES';
			}
		}
	};

	composeShipperInformation = () => {
		const {
			buyerName,
			phone1,
			address1,
			address2,
			address3,
			city,
			zipCode,
			countryStateCode,
			countryCode,
		} = this.form.values();

		const shipperInformation = {
			personName: buyerName,
			phoneNumber: phone1,
			streetLines: [address1, address2, address3].filter(value => value), // Removes empty string, null or undefined from the array.
			city: city,
			countryCode: countryCode,
			stateOrProvinceCode: countryStateCode,
			postalCode: zipCode,
		};

		return shipperInformation;
	};

	@action.bound
	handleFocusChange = async (item, isBlur) => {
		let matchingItem = this.selectedOrder.orderLines.find(i => i.sku === item.sku);
		if (isBlur) {
			if (matchingItem.checkedQuantity == '') {
				matchingItem.checkedQuantity = 0;
			}
		} else {
			if (matchingItem.checkedQuantity == 0) {
				matchingItem.checkedQuantity = '';
			}
		}
	};

	@action.bound
	handleItemQuantityChange = async (selectedItem, isIncrease, event) => {
		_.forEach(this.selectedOrder.orderLines, orderLine => {
			const isOnePerBox = orderLine.product.onePerBox;
			if (selectedItem.sku === orderLine.sku && selectedItem.lineNumber === orderLine.lineNumber) {
				orderLine.disabled = false;
				if (isOnePerBox && isIncrease && !event) {
					// One per box = true, quantity increase.
					orderLine.checkedQuantity = 1;
					orderLine.disabled = false;
					orderLine.isChecked = isIncrease;
				} else if (isOnePerBox && !isIncrease && !event) {
					// One per box = true, quantity decrease.
					orderLine.checkedQuantity = 0;
					orderLine.disabled = false;
					orderLine.isChecked = false;
					orderLine.isPartiallyChecked = false;
				} else if (!isOnePerBox && isIncrease && !event) {
					// One per box = false, quantity increase.
					orderLine.checkedQuantity = ++orderLine.checkedQuantity;
					if (orderLine.checkedQuantity > orderLine.quantity) {
						orderLine.checkedQuantity = orderLine.quantity;
					}
					orderLine.isChecked = orderLine.checkedQuantity === orderLine.quantity;
					orderLine.isPartiallyChecked =
						orderLine.checkedQuantity !== orderLine.quantity && orderLine.checkedQuantity > 0;
				} else if (!isOnePerBox && !isIncrease && !event) {
					// One per box = false, quantity decrease.
					orderLine.checkedQuantity = --orderLine.checkedQuantity;
					if (orderLine.checkedQuantity < 0) {
						orderLine.checkedQuantity = 0;
					}
					orderLine.isChecked = orderLine.checkedQuantity === orderLine.quantity;
					orderLine.isPartiallyChecked =
						orderLine.checkedQuantity !== orderLine.quantity && orderLine.checkedQuantity > 0;
				} else if (isOnePerBox && event) {
					// One per box = true, checkbox increase/decrease.
					orderLine.checkedQuantity = isIncrease ? 1 : 0;
					orderLine.isChecked = isIncrease;
					orderLine.isPartiallyChecked = false;
				} else if (!isOnePerBox && event) {
					// One per box = false, checkbox increase/decrease.
					orderLine.checkedQuantity = isIncrease ? orderLine.quantity : 0;
					orderLine.isChecked = isIncrease;
					orderLine.isPartiallyChecked = false;
				}

				if (
					(isOnePerBox && orderLine.isChecked && this.selectedOrder.orderLines.length > 1) ||
					(this.selectedOrder.orderLines.length === 1 && isOnePerBox && orderLine.isChecked)
				) {
					this.onePerBoxMessage = 'ORDERVIEW.RETURNLABELVIEW.ONEPERBOXMESSAGE';
				} else {
					this.onePerBoxMessage = '';
				}
			} else {
				if (isOnePerBox) {
					if (!isIncrease) {
						orderLine.disabled = false;
					} else {
						orderLine.disabled = true;
					}
				} else if (!selectedItem.product.onePerBox) {
					orderLine.disabled = false;
				} else {
					orderLine.disabled = true;
				}
			}
		});

		// If one of the items selected is not one per box (OnePerBox = false), all one per box items should be disabled.
		// If none of the items is selected, none is disabled for selections.
		this.checkSelectedItems();

		// Box is required if at least one item is selected, else it is hidden, not matter if OnePerBox = true.
		this.isBoxRequired = _.sum(_.map(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity)) > 1;

		if (this.isFormValid && _.some(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity !== 0)) {
			this.debouncedGetRates();
		}
	};

	@action.bound
	handleItemQuantityOnInputChange = async (item, value) => {
		let getNewRates = false;
		let matchingItem = this.selectedOrder.orderLines.find(i => i.sku === item.sku && i.lineNumber === item.lineNumber);
		if (!value) {
			getNewRates = true;
			matchingItem.checkedQuantity = 0;
		}
		if (value >= 0 && value <= matchingItem.quantity) {
			getNewRates = true;
			matchingItem.checkedQuantity = value;
		}
		matchingItem.isPartiallyChecked =
			matchingItem.checkedQuantity !== matchingItem.quantity && matchingItem.checkedQuantity > 0;
		matchingItem.isChecked = matchingItem.checkedQuantity === matchingItem.quantity;

		// If one of the items selected is not one per box (OnePerBox = false), all one per box items should be disabled.
		// If none of the items is selected, none is disabled for selections.
		this.checkSelectedItems();

		// Box is required if more then one quantity is selected.
		this.isBoxRequired = _.sum(_.map(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity)) > 1;

		if (
			this.isFormValid &&
			getNewRates &&
			_.some(this.selectedOrder.orderLines, item => {
				return item.checkedQuantity != 0;
			})
		) {
			this.debouncedGetRates();
		}
	};

	@action.bound
	onBoxChange = async value => {
		this.form.$('carrierServiceId').set('rules', '');
		if (_.get(value, 'label') === 'Add new template') {
			this.boxDropdownStore.value = null; // clear() not used in order to avoid triggering onChange callback and causing infinite loop
			this.boxForm.$('boxId').clear();
			this.showCreateTemplate = true;
			this.boxForm.$('boxId').resetValidation();
			this.boxForm.$('boxId').set('rules', '');
		} else {
			this.boxForm.$('boxId').set('rules', this.isBoxRequired ? 'required' : '');
			if (value) {
				this.showCreateTemplate = false;
				await this.validateForm();
				if (
					this.isFormValid &&
					_.some(this.selectedOrder.orderLines, item => {
						return item.checkedQuantity != 0;
					})
				) {
					await this.getRates();
				}
			} else {
				this.isFormValid = false;
			}
		}
	};

	@action.bound
	onCreateTemplateCancel = () => {
		this.boxForm.reset();
		this.boxDropdownStore.clear();
		this.boxForm.$('boxId').invalidate();
		this.showCreateTemplate = false;
		this.boxForm.$('boxId').set('rules', this.isBoxRequired ? 'required' : '');
	};

	@action.bound
	onCreateTemplateSubmit = async () => {
		try {
			const { name, width, height, length } = this.boxForm.values();
			const boxValues = {
				name,
				width,
				height,
				length,
			};
			const response = await this.lookupStore.createLookup(boxValues, lookupRouteName.box, sourceOfChange.createLookup);
			if (response.message) {
				this.boxForm.$('name').invalidate(response.message);
			} else {
				var createdBox = this.lookupStore.item;
				if (!createdBox) return;

				this.showCreateTemplate = false;
				this.boxForm.$('name').clear();
				this.boxForm.$('height').clear();
				this.boxForm.$('width').clear();
				this.boxForm.$('length').clear();
				this.boxDropdownStore.options.push({ value: createdBox.id, label: createdBox.name, extra: createdBox });
				this.boxDropdownStore.setValue(createdBox);
				this.boxForm.$('boxId').set('rules', this.isBoxRequired ? 'required' : '');
				await this.validateForm();
				if (this.isFormValid) {
					await this.getRates();
				}
			}
		} catch (err) {}
	};

	@action.bound
	calculateCheckedQuantity = order => {
		_.forEach(order.orderLines, orderLine => {
			if (!orderLine.product.isShippable) {
				orderLine.disabled = true;
				orderLine.checkedQuantity = 0;
				return;
			}
			orderLine.isChecked = order.orderLines.length == 1 ? true : false;
			orderLine.isPartiallyChecked = false;
			// If is one per box.
			if (order.orderLines.length == 1 && orderLine.product.onePerBox) {
				orderLine.checkedQuantity = 1;
			} else if (order.orderLines.length == 1 && !orderLine.product.onePerBox) {
				orderLine.checkedQuantity = orderLine.quantity;
				// If is not one per box.
			} else {
				orderLine.checkedQuantity = 0;
			}
		});

		// Box is required if at least one item is selected, else it is hidden, not matter if OnePerBox = true.
		this.isBoxRequired = _.sum(_.map(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity)) > 1;
	};

	@action.bound
	checkSelectedItems = () => {
		if (
			_.some(this.selectedOrder.orderLines, orderLine => !orderLine.product.onePerBox && orderLine.checkedQuantity > 0)
		) {
			_.forEach(this.selectedOrder.orderLines, orderLine => {
				if (orderLine.product.onePerBox) {
					orderLine.disabled = true;
				}
			});
		} else if (_.every(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity === 0)) {
			_.forEach(this.selectedOrder.orderLines, orderLine => {
				orderLine.disabled = false;
			});
		}
	};

	@action.bound
	getItemsForReturn = () => {
		const itemsForReturn = [];
		_.forEach(this.selectedOrder.orderLines, orderLine => {
			if (orderLine.checkedQuantity > 0) {
				itemsForReturn.push({
					productId: orderLine.productId,
					quantity: orderLine.checkedQuantity,
					product: orderLine.product,
					lineNumber: orderLine.lineNumber,
				});
			}
		});
		return itemsForReturn;
	};
}

export default ReturnLabelViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/ReturnLabelViewStore.js